import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { daily } from "./provider";

const syncModuleId = "strassenverzeichnis";

export type Strasse = {
  readonly id: string;
  readonly name: string;
  readonly hausnummer?: string;
  readonly postleitzahl?: string;
  readonly ort?: string;
  readonly ortsteil?: string;
  readonly latitude?: number;
  readonly longitude?: number;
};

function useStrassenverzeichnisSyncModule(): SyncModule<
  ReadonlyArray<Strasse>
> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `tasks:${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // The street directory is a new-Datenportal-only resource — the legacy
      // portal has no equivalent endpoint, so we simply don't fetch anything for it.
      fetchData: useNewApi && baseUrl
        ? () => fetchStrassenverzeichnisNew(portalKey, baseUrl)
        : async () => [],
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchStrassenverzeichnisNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<Strasse>> {
  const response = await fetch(`${baseUrl}/api/device/sync/streets`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.streets ?? []).map(parseStrasseNew);
}

function parseStrasseNew(entry: any): Strasse {
  return {
    id: entry.id,
    name: entry.name,
    hausnummer: entry.houseNumber || undefined,
    postleitzahl: entry.postalCode || undefined,
    ort: entry.city || undefined,
    ortsteil: entry.district || undefined,
    latitude: entry.latitude ?? undefined,
    longitude: entry.longitude ?? undefined,
  };
}

export function useStrassenverzeichnisSync() {
  const module = useStrassenverzeichnisSyncModule();
  useSyncModuleRegistration(module);
}

export function useStrassenverzeichnis(): ReadonlyArray<Strasse> {
  return useSyncData(syncModuleId) ?? [];
}
